import React, { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Box, Button, Menu, MenuItem } from "@mui/material";

import { notApplicableStatuses, approvalFlowStatuses, basicStatuses, UtilityBillStatusKey, UTILITY_BILL_FORM_VIEW, UTILITY_BILL_COMMENTS_VIEW, UTILITY_BILL_ALL_DATAENTRIES_VIEW } from "pages";
import { EsgArrowDown } from "assets";
import { combineDropdownOptions, getFromLocalStorage, getLoggedUserData, isFeatureEnabled } from "utils";
import { useGetConfigParameter, useNotify, useUtilityBillWSConnection } from "hooks";
import { CloseButton, COLORS, Loading, RoutedTabs, TYPOGRAPHY_COMMON_STYLES, UTILITY_BILL_STATUS_TO_BACKGROUND_COLOR, UTILITY_BILL_STATUS_TO_MESSAGE } from "components";
import { changeUtilityBillStatus } from "api";

import { UtilityBillFormProps } from "./types";
import { checkURL } from "./helpers";

export const UtilityBillForm: React.FC<UtilityBillFormProps> = ({
  utilityBill,
  onClose,
  billId,
}) => {
  const notify = useNotify();
  const loggedUserData = getLoggedUserData();
  const { data: configParameter } = useGetConfigParameter();

  const { mutateAsync: updateBillStatus, isLoading: isStatusLoading } = useMutation(changeUtilityBillStatus);
  useUtilityBillWSConnection(billId || "");

  const showDropDown = checkURL(window.location.pathname);
  const isAuditor = loggedUserData?.roles?.includes("AUDITOR");
  const isDataProvider = loggedUserData?.roles?.includes("DATA_PROVIDER");
  const algorithm = getFromLocalStorage("algorithm");
  const utilityBillStatus: UtilityBillStatusKey | undefined = (utilityBill as any)?.statusFilter as UtilityBillStatusKey | undefined;
  const isApproved = utilityBillStatus ? (utilityBillStatus === "Approved (Signed Off)" || utilityBillStatus === "Approved (Locked)") : false;

  const isApprovalFlowValueOn = isFeatureEnabled(configParameter, "second_layer_approval_feature");
  const isAdditionalStatusesOn = isFeatureEnabled(configParameter, "unusable_status_enabled");

  // Right below 👇 we have basicStatuses always on, that means = true;
  const dropDownStatusOption: Array<{ label: string, name: string }> = combineDropdownOptions(
    [true, isApprovalFlowValueOn, isAdditionalStatusesOn],
    [basicStatuses, approvalFlowStatuses, notApplicableStatuses],
  );

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("");

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = async (status: any) => {
    setAnchorEl(null);

    if (!status) {
      return;
    }

    const statusChangePayload = {
      "id": billId,
      status
    };

    try {
      await updateBillStatus(statusChangePayload, {
        onSuccess: () => {
          setSelectedStatus(status);
        },
      });
    } catch (updateError: any) {
      const errorMessage = typeof updateError?.response?.data === "string"
        ? updateError.response.data
        : "Failed to update utility bill status";
      notify.error(errorMessage);
    }
  };

  useEffect(() => {
    if (utilityBillStatus && UTILITY_BILL_STATUS_TO_MESSAGE[utilityBillStatus]) {
      setSelectedStatus(UTILITY_BILL_STATUS_TO_MESSAGE[utilityBillStatus]);
    }
  }, [utilityBillStatus]);

  if (isStatusLoading) {
    return <Loading />;
  };

  return (
    <>
      <Box
        sx={{
          width: "100%",
          height: "34px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {showDropDown ? <Box sx={{ display: "flex" }}>
          <Button
            endIcon={isApproved ? "" : <EsgArrowDown />}
            variant="outlined"
            disabled={isApproved || isAuditor}
            sx={{ alignItems: "center", gap: 2, display: "flex", justifyContent: "center", borderRadius: "8px" }}
            onClick={handleClick}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, padding: "2px 6px", borderRadius: "12px" }}>
              <Box
                sx={{
                  bgcolor: (() => {
                    if (!utilityBillStatus) return COLORS.disabled;
                    const bgColor = UTILITY_BILL_STATUS_TO_BACKGROUND_COLOR[utilityBillStatus];
                    return bgColor || COLORS.disabled;
                  })(),
                  height: "10px",
                  width: "10px",
                  borderRadius: "50%",
                }}
              />
              <Box sx={{ color: "black" }}>{selectedStatus}</Box>
            </Box>
          </Button>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => handleClose(null)}
          >
            {dropDownStatusOption.map((status: { label: string, name: string }) => (
              <MenuItem
                disabled={isDataProvider && status.name === "Reviewed"}
                sx={{
                  fontWeight: TYPOGRAPHY_COMMON_STYLES.fontWeightSemiBold,
                  fontSize: 14,
                  lineHeight: "18px",
                  color: "#90959E",
                  "&:hover": {
                    backgroundColor: "#F6F8FA",
                    borderRadius: 5,
                    color: COLORS.Romance,
                  },
                  "&.Mui-selected": {
                    backgroundColor: "#F6F8FA",
                    borderRadius: 5,
                    color: COLORS.Romance,
                  },
                }}
                key={status.name}
                onClick={() => handleClose(status.name)}
              >
                {status.label}
              </MenuItem>
            ))}
          </Menu>
        </Box> : <Box />}
        {(algorithm !== "IDP Fallback" || algorithm !== "IDP A/B Testing") && (
          <CloseButton onClick={onClose} variant="outlined" />
        )}
      </Box>
      <Box
        sx={{
          width: "100%",
          height: "calc(100vh - 167px)",
          mt: 2,
        }}
      >
        <RoutedTabs
          tabs={[
            {
              label: UTILITY_BILL_ALL_DATAENTRIES_VIEW.TITLE,
              to: UTILITY_BILL_ALL_DATAENTRIES_VIEW.ABSOLUTE_PATH(utilityBill.id),
              disable: false,
            },
            {
              icon: UTILITY_BILL_FORM_VIEW.ICON,
              label: UTILITY_BILL_FORM_VIEW.TITLE,
              to: UTILITY_BILL_FORM_VIEW.ABSOLUTE_PATH(utilityBill.id),
              disable: false,
            },
            {
              icon: UTILITY_BILL_COMMENTS_VIEW.ICON,
              label: UTILITY_BILL_COMMENTS_VIEW.TITLE(utilityBill.numberOfComments),
              to: UTILITY_BILL_COMMENTS_VIEW.ABSOLUTE_PATH(utilityBill.id),
              disable: false,
            },
          ]}
        />
      </Box>
    </>
  );
};
